import type { PoetProfile } from '../types'

/**
 * Life trails for the poets that carry most of the corpus. Stations follow
 * standard biographies (旧/新唐书、宋史本传与通行年谱); where a year or site is
 * disputed the label says so with 约 or 传. Coordinates mark the historical
 * seat of the town, not a precise building, and are meant for a map at
 * province-to-prefecture scale.
 */
export const poets: PoetProfile[] = [
  {
    id: 'qu-yuan',
    name: '屈原',
    dynasty: 'pre-qin',
    styleName: '名平，字原',
    epithet: '楚辞之祖',
    birthYear: -340,
    deathYear: -278,
    lifeLabel: '约前340—前278',
    hometown: { placeName: '秭归 · 乐平里', longitude: 110.98, latitude: 30.83 },
    summary: '楚国宗臣，任左徒、三闾大夫，主张联齐抗秦，两度被放逐，终自沉汨罗。',
    stations: [
      { year: -340, yearLabel: '约前340年', placeName: '秭归', longitude: 110.98, latitude: 30.83, event: '生于楚国丹阳秭归' },
      { year: -318, yearLabel: '约前318年', placeName: '郢都', longitude: 112.18, latitude: 30.43, event: '任左徒，入则图议国事' },
      { year: -304, yearLabel: '约前304年', placeName: '汉北', longitude: 112.2, latitude: 31.7, event: '遭谗见疏，初放汉北' },
      { year: -296, yearLabel: '约前296年', placeName: '溆浦', longitude: 110.59, latitude: 27.91, event: '再放江南，涉沅湘至溆浦' },
      { year: -278, yearLabel: '前278年', placeName: '汨罗江', longitude: 113.08, latitude: 28.8, event: '郢都破后自沉汨罗' },
    ],
  },
  {
    id: 'cao-cao',
    name: '曹操',
    dynasty: 'han',
    styleName: '字孟德',
    epithet: '建安风骨之首',
    birthYear: 155,
    deathYear: 220,
    lifeLabel: '155—220',
    hometown: { placeName: '沛国谯县', longitude: 115.78, latitude: 33.85 },
    summary: '东汉末年统一北方的政治家、诗人，以乐府旧题写时事，开建安文学风气。',
    stations: [
      { year: 155, yearLabel: '155年', placeName: '谯县', longitude: 115.78, latitude: 33.85, event: '生于沛国谯县' },
      { year: 196, yearLabel: '196年', placeName: '许都', longitude: 113.85, latitude: 34.03, event: '迎汉献帝都许' },
      { year: 200, yearLabel: '200年', placeName: '官渡', longitude: 114.0, latitude: 34.72, event: '官渡之战破袁绍' },
      { year: 207, yearLabel: '207年', placeName: '碣石', longitude: 119.18, latitude: 39.72, event: '北征乌桓，东临碣石' },
      { year: 208, yearLabel: '208年', placeName: '赤壁', longitude: 113.9, latitude: 29.87, event: '南征荆州，败于赤壁' },
      { year: 210, yearLabel: '210年', placeName: '邺城', longitude: 114.36, latitude: 36.33, event: '筑铜雀台，邺下文人云集' },
      { year: 220, yearLabel: '220年', placeName: '洛阳', longitude: 112.6, latitude: 34.73, event: '病逝洛阳' },
    ],
    companions: ['曹植'],
  },
  {
    id: 'cao-zhi',
    name: '曹植',
    dynasty: 'wei-jin',
    styleName: '字子建',
    epithet: '陈思王',
    birthYear: 192,
    deathYear: 232,
    lifeLabel: '192—232',
    hometown: { placeName: '沛国谯县', longitude: 115.78, latitude: 33.85 },
    summary: '曹操之子，少以才名，前期作品慷慨任气，曹丕即位后屡遭迁徙，诗风转为沉郁。',
    stations: [
      { year: 210, yearLabel: '210年', placeName: '邺城', longitude: 114.36, latitude: 36.33, event: '登铜雀台作赋，才名初显' },
      { year: 221, yearLabel: '221年', placeName: '鄄城', longitude: 115.5, latitude: 35.56, event: '曹丕即位，贬为鄄城侯' },
      { year: 223, yearLabel: '223年', placeName: '洛阳', longitude: 112.6, latitude: 34.73, event: '朝京师，与白马王彪分道' },
      { year: 229, yearLabel: '229年', placeName: '东阿', longitude: 116.25, latitude: 36.33, event: '徙封东阿' },
      { year: 232, yearLabel: '232年', placeName: '陈', longitude: 114.89, latitude: 33.73, event: '改封陈王，同年病卒' },
    ],
    companions: ['曹操'],
  },
  {
    id: 'tao-yuanming',
    name: '陶渊明',
    dynasty: 'wei-jin',
    styleName: '字元亮，号五柳先生',
    epithet: '田园诗人之宗',
    birthYear: 365,
    deathYear: 427,
    lifeLabel: '约365—427',
    hometown: { placeName: '浔阳柴桑', longitude: 115.88, latitude: 29.46 },
    summary: '五仕五隐，四十一岁辞彭泽令归田，此后躬耕庐山脚下二十余年。',
    stations: [
      { year: 365, yearLabel: '约365年', placeName: '柴桑', longitude: 115.88, latitude: 29.46, event: '生于浔阳柴桑' },
      { year: 393, yearLabel: '393年', placeName: '江州', longitude: 115.99, latitude: 29.71, event: '初仕为江州祭酒' },
      { year: 400, yearLabel: '400年', placeName: '江陵', longitude: 112.24, latitude: 30.33, event: '入桓玄幕' },
      { year: 405, yearLabel: '405年', placeName: '彭泽', longitude: 116.55, latitude: 29.9, event: '任彭泽令八十余日，解印去职' },
      { year: 427, yearLabel: '427年', placeName: '柴桑', longitude: 115.88, latitude: 29.46, event: '终老田园' },
    ],
  },
  {
    id: 'xie-lingyun',
    name: '谢灵运',
    dynasty: 'southern-northern',
    epithet: '山水诗之祖',
    birthYear: 385,
    deathYear: 433,
    lifeLabel: '385—433',
    hometown: { placeName: '会稽始宁', longitude: 120.87, latitude: 29.9 },
    summary: '东晋名将谢玄之孙，袭封康乐公。出守永嘉后遍游山水，把山水从玄言里解放出来。',
    stations: [
      { year: 385, yearLabel: '385年', placeName: '始宁', longitude: 120.87, latitude: 29.9, event: '生于会稽始宁墅' },
      { year: 422, yearLabel: '422年', placeName: '永嘉', longitude: 120.65, latitude: 28.02, event: '出为永嘉太守，遍游郡中山水' },
      { year: 424, yearLabel: '424年', placeName: '始宁', longitude: 120.87, latitude: 29.9, event: '辞官归始宁，营别墅' },
      { year: 433, yearLabel: '433年', placeName: '广州', longitude: 113.26, latitude: 23.13, event: '以谋逆罪流广州，弃市' },
    ],
  },
  {
    id: 'xie-tiao',
    name: '谢朓',
    dynasty: 'southern-northern',
    styleName: '字玄晖',
    epithet: '小谢',
    birthYear: 464,
    deathYear: 499,
    lifeLabel: '464—499',
    hometown: { placeName: '陈郡阳夏', longitude: 114.9, latitude: 34.06 },
    summary: '竟陵八友之一，永明体代表。出守宣城，后世因称谢宣城，李白一生推崇。',
    stations: [
      { year: 490, yearLabel: '约490年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '游竟陵王西邸，与沈约等倡永明体' },
      { year: 495, yearLabel: '495年', placeName: '宣城', longitude: 118.75, latitude: 30.95, event: '出守宣城，建高斋' },
      { year: 499, yearLabel: '499年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '卷入宗室之争，下狱死' },
    ],
  },
  {
    id: 'wang-bo',
    name: '王勃',
    dynasty: 'tang',
    styleName: '字子安',
    epithet: '初唐四杰',
    birthYear: 650,
    deathYear: 676,
    lifeLabel: '约650—676',
    hometown: { placeName: '绛州龙门', longitude: 110.72, latitude: 35.6 },
    summary: '少年成名，因《檄英王鸡》被逐出沛王府。南下交趾省父途中过洪州作滕王阁序，渡海溺水而亡。',
    stations: [
      { year: 650, yearLabel: '约650年', placeName: '龙门', longitude: 110.72, latitude: 35.6, event: '生于绛州龙门' },
      { year: 666, yearLabel: '666年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '应幽素科及第，为沛王府修撰' },
      { year: 670, yearLabel: '669—671年', placeName: '蜀中', longitude: 104.07, latitude: 30.67, event: '被逐出王府，漫游巴蜀' },
      { year: 675, yearLabel: '675年', placeName: '洪州', longitude: 115.88, latitude: 28.68, event: '赴交趾途经洪州，作滕王阁序' },
      { year: 676, yearLabel: '676年', placeName: '南海', longitude: 108.6, latitude: 21.3, event: '渡海省父，溺水惊悸而卒' },
    ],
  },
  {
    id: 'chen-ziang',
    name: '陈子昂',
    dynasty: 'tang',
    styleName: '字伯玉',
    birthYear: 659,
    deathYear: 700,
    lifeLabel: '659—700',
    hometown: { placeName: '梓州射洪', longitude: 105.38, latitude: 30.87 },
    summary: '倡汉魏风骨，反对齐梁绮靡。随军东征契丹时登幽州台，一歌成绝唱。',
    stations: [
      { year: 659, yearLabel: '659年', placeName: '射洪', longitude: 105.38, latitude: 30.87, event: '生于梓州射洪' },
      { year: 684, yearLabel: '684年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '进士及第，上书武后' },
      { year: 696, yearLabel: '696年', placeName: '幽州', longitude: 116.4, latitude: 39.9, event: '随武攸宜东征契丹，登幽州台' },
      { year: 700, yearLabel: '700年', placeName: '射洪', longitude: 105.38, latitude: 30.87, event: '归乡，为县令段简所陷，死于狱中' },
    ],
  },
  {
    id: 'he-zhizhang',
    name: '贺知章',
    dynasty: 'tang',
    styleName: '字季真',
    epithet: '四明狂客',
    birthYear: 659,
    deathYear: 744,
    lifeLabel: '659—744',
    hometown: { placeName: '越州永兴', longitude: 120.27, latitude: 30.17 },
    summary: '少小离家，在长安为官五十年，晚年见李白而呼为谪仙，八十六岁告老还乡。',
    stations: [
      { year: 695, yearLabel: '695年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '进士及第，入仕长安' },
      { year: 742, yearLabel: '742年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '于紫极宫初见李白，称其谪仙人' },
      { year: 744, yearLabel: '744年', placeName: '越州永兴', longitude: 120.27, latitude: 30.17, event: '上疏请为道士，还乡' },
    ],
    companions: ['李白'],
  },
  {
    id: 'zhang-jiuling',
    name: '张九龄',
    dynasty: 'tang',
    styleName: '字子寿',
    epithet: '岭南第一人',
    birthYear: 678,
    deathYear: 740,
    lifeLabel: '678—740',
    hometown: { placeName: '韶州曲江', longitude: 113.6, latitude: 24.81 },
    summary: '开元名相，以直言见称。罢相后贬荆州长史，诗风清淡，下启王孟。',
    stations: [
      { year: 678, yearLabel: '678年', placeName: '曲江', longitude: 113.6, latitude: 24.81, event: '生于韶州曲江' },
      { year: 716, yearLabel: '716年', placeName: '大庾岭', longitude: 114.36, latitude: 25.4, event: '奏开大庾岭新路' },
      { year: 733, yearLabel: '733年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '拜中书令，居相位' },
      { year: 737, yearLabel: '737年', placeName: '荆州', longitude: 112.24, latitude: 30.33, event: '受李林甫排挤，贬荆州长史' },
      { year: 740, yearLabel: '740年', placeName: '曲江', longitude: 113.6, latitude: 24.81, event: '回乡扫墓，卒于曲江' },
    ],
    companions: ['孟浩然', '王维'],
  },
  {
    id: 'meng-haoran',
    name: '孟浩然',
    dynasty: 'tang',
    epithet: '孟襄阳',
    birthYear: 689,
    deathYear: 740,
    lifeLabel: '689—740',
    hometown: { placeName: '襄州襄阳', longitude: 112.14, latitude: 32.02 },
    summary: '盛唐山水田园诗人，一生未仕。隐鹿门山，游吴越，入张九龄幕，终老襄阳。',
    stations: [
      { year: 689, yearLabel: '689年', placeName: '襄阳', longitude: 112.14, latitude: 32.02, event: '生于襄阳' },
      { year: 715, yearLabel: '约715年', placeName: '鹿门山', longitude: 112.18, latitude: 31.98, event: '隐居鹿门山读书' },
      { year: 728, yearLabel: '728年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '入京应进士不第' },
      { year: 730, yearLabel: '730年', placeName: '吴越', longitude: 119.28, latitude: 29.48, event: '漫游吴越，泊建德江' },
      { year: 737, yearLabel: '737年', placeName: '荆州', longitude: 112.24, latitude: 30.33, event: '入荆州长史张九龄幕' },
      { year: 740, yearLabel: '740年', placeName: '襄阳', longitude: 112.14, latitude: 32.02, event: '王昌龄来访，宴饮后疽发而卒' },
    ],
    companions: ['李白', '王维', '王昌龄', '张九龄'],
  },
  {
    id: 'wang-changling',
    name: '王昌龄',
    dynasty: 'tang',
    styleName: '字少伯',
    epithet: '七绝圣手',
    birthYear: 698,
    deathYear: 757,
    lifeLabel: '约698—约757',
    hometown: { placeName: '京兆长安', longitude: 108.95, latitude: 34.27 },
    summary: '早年游历西北边塞，边塞七绝冠绝一时。一贬岭南，再贬龙标，安史乱中被杀。',
    stations: [
      { year: 724, yearLabel: '约724年', placeName: '河陇', longitude: 102.64, latitude: 37.93, event: '游历西北边塞' },
      { year: 727, yearLabel: '727年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '进士及第，补秘书省校书郎' },
      { year: 740, yearLabel: '740年', placeName: '襄阳', longitude: 112.14, latitude: 32.02, event: '北归途中访孟浩然' },
      { year: 741, yearLabel: '741年', placeName: '江宁', longitude: 118.78, latitude: 32.06, event: '任江宁丞，润州送辛渐' },
      { year: 748, yearLabel: '约748年', placeName: '龙标', longitude: 109.82, latitude: 27.21, event: '贬龙标尉，李白遥寄诗' },
      { year: 757, yearLabel: '约757年', placeName: '亳州', longitude: 115.78, latitude: 33.85, event: '乱中归乡，为刺史闾丘晓所杀' },
    ],
    companions: ['李白', '孟浩然'],
  },
  {
    id: 'wang-wei',
    name: '王维',
    dynasty: 'tang',
    styleName: '字摩诘',
    epithet: '诗佛',
    birthYear: 701,
    deathYear: 761,
    lifeLabel: '701—761',
    hometown: { placeName: '河东蒲州', longitude: 110.31, latitude: 34.84 },
    summary: '诗、画、乐兼擅。早年出使塞上，中年经营辋川别业，安史乱中陷贼，晚年官至尚书右丞。',
    stations: [
      { year: 717, yearLabel: '717年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '十七岁客居长安，重阳忆山东兄弟' },
      { year: 721, yearLabel: '721年', placeName: '济州', longitude: 116.2, latitude: 36.45, event: '进士及第，旋贬济州司仓参军' },
      { year: 737, yearLabel: '737年', placeName: '凉州', longitude: 102.64, latitude: 37.93, event: '以监察御史出塞，宣慰河西' },
      { year: 740, yearLabel: '740年', placeName: '襄阳', longitude: 112.15, latitude: 32.05, event: '知南选，泛舟汉江' },
      { year: 745, yearLabel: '约745年', placeName: '辋川', longitude: 109.36, latitude: 34.02, event: '得辋川别业，半官半隐' },
      { year: 756, yearLabel: '756年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '长安陷落被俘，拘于洛阳菩提寺' },
      { year: 761, yearLabel: '761年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '官尚书右丞，卒' },
    ],
    companions: ['孟浩然', '张九龄'],
  },
  {
    id: 'li-bai',
    name: '李白',
    dynasty: 'tang',
    styleName: '字太白，号青莲居士',
    epithet: '诗仙',
    birthYear: 701,
    deathYear: 762,
    lifeLabel: '701—762',
    hometown: { placeName: '绵州昌隆 · 青莲乡', longitude: 104.75, latitude: 31.78 },
    summary: '五岁随父入蜀，二十四岁仗剑出川，此后漫游半个中国。供奉翰林不足两年，晚年因永王案流放夜郎，遇赦东归。',
    stations: [
      { year: 705, yearLabel: '705年', placeName: '青莲乡', longitude: 104.75, latitude: 31.78, event: '随父迁居绵州昌隆' },
      { year: 725, yearLabel: '725年', placeName: '峨眉山', longitude: 103.33, latitude: 29.52, event: '辞亲远游，出峨眉下渝州' },
      { year: 727, yearLabel: '727年', placeName: '安陆', longitude: 113.69, latitude: 31.26, event: '娶故相许圉师孙女，酒隐安陆' },
      { year: 742, yearLabel: '742年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '奉诏入京，供奉翰林' },
      { year: 744, yearLabel: '744年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '赐金放还，与杜甫相识' },
      { year: 753, yearLabel: '753年', placeName: '宣城', longitude: 118.75, latitude: 30.95, event: '往来宣城，追慕谢朓' },
      { year: 757, yearLabel: '757年', placeName: '浔阳', longitude: 115.99, latitude: 29.71, event: '坐永王璘案系浔阳狱' },
      { year: 759, yearLabel: '759年', placeName: '白帝城', longitude: 109.57, latitude: 31.05, event: '流放夜郎途中遇赦东还' },
      { year: 762, yearLabel: '762年', placeName: '当涂', longitude: 118.49, latitude: 31.55, event: '依族叔李阳冰，病卒当涂' },
    ],
    companions: ['杜甫', '孟浩然', '王昌龄', '贺知章', '高适'],
  },
  {
    id: 'gao-shi',
    name: '高适',
    dynasty: 'tang',
    styleName: '字达夫',
    birthYear: 704,
    deathYear: 765,
    lifeLabel: '约704—765',
    hometown: { placeName: '渤海蓨县', longitude: 116.27, latitude: 37.69 },
    summary: '前半生落拓宋中，五十岁后入哥舒翰幕，官至剑南西川节度使，是唐代诗人中最显达者。',
    stations: [
      { year: 731, yearLabel: '约731年', placeName: '睢阳', longitude: 115.65, latitude: 34.42, event: '寓居宋中，耕钓为业' },
      { year: 744, yearLabel: '744年', placeName: '梁宋', longitude: 114.35, latitude: 34.79, event: '与李白、杜甫同游梁宋' },
      { year: 752, yearLabel: '752年', placeName: '河西', longitude: 102.64, latitude: 37.93, event: '入哥舒翰幕掌书记' },
      { year: 763, yearLabel: '763年', placeName: '成都', longitude: 104.07, latitude: 30.67, event: '任剑南西川节度使' },
      { year: 765, yearLabel: '765年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '召还为刑部侍郎，卒' },
    ],
    companions: ['李白', '杜甫', '岑参'],
  },
  {
    id: 'du-fu',
    name: '杜甫',
    dynasty: 'tang',
    styleName: '字子美，号少陵野老',
    epithet: '诗圣',
    birthYear: 712,
    deathYear: 770,
    lifeLabel: '712—770',
    hometown: { placeName: '河南巩县', longitude: 113.02, latitude: 34.75 },
    summary: '困守长安十年，身陷安史之乱，弃官西行入蜀，晚年漂泊荆湘，病逝湘江舟中。其诗被称为诗史。',
    stations: [
      { year: 712, yearLabel: '712年', placeName: '巩县', longitude: 113.02, latitude: 34.75, event: '生于巩县南瑶湾' },
      { year: 736, yearLabel: '736年', placeName: '泰山', longitude: 117.1, latitude: 36.25, event: '游齐赵，望岳' },
      { year: 744, yearLabel: '744年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '与李白相识，同游梁宋' },
      { year: 746, yearLabel: '746年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '入长安求仕，困守十年' },
      { year: 757, yearLabel: '757年', placeName: '凤翔', longitude: 107.4, latitude: 34.52, event: '逃出长安投奔肃宗，拜左拾遗' },
      { year: 759, yearLabel: '759年', placeName: '秦州', longitude: 105.72, latitude: 34.58, event: '弃官西行，客秦州、同谷' },
      { year: 760, yearLabel: '760年', placeName: '成都', longitude: 104.03, latitude: 30.66, event: '营浣花溪草堂' },
      { year: 766, yearLabel: '766年', placeName: '夔州', longitude: 109.57, latitude: 31.05, event: '寓居夔州近两年' },
      { year: 768, yearLabel: '768年', placeName: '岳阳', longitude: 113.09, latitude: 29.37, event: '出峡漂泊荆湘，登岳阳楼' },
      { year: 770, yearLabel: '770年', placeName: '湘江', longitude: 112.95, latitude: 28.5, event: '卒于潭州往岳阳的舟中' },
    ],
    companions: ['李白', '高适', '岑参'],
  },
  {
    id: 'cen-shen',
    name: '岑参',
    dynasty: 'tang',
    epithet: '边塞诗人',
    birthYear: 715,
    deathYear: 770,
    lifeLabel: '约715—770',
    hometown: { placeName: '荆州江陵', longitude: 112.19, latitude: 30.35 },
    summary: '两度出塞，远至安西、北庭，是唐代亲历西域最远的诗人，边塞诗奇丽峭拔。',
    stations: [
      { year: 744, yearLabel: '744年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '进士及第' },
      { year: 749, yearLabel: '749年', placeName: '安西', longitude: 82.96, latitude: 41.72, event: '首次出塞，入高仙芝安西幕' },
      { year: 754, yearLabel: '754年', placeName: '轮台', longitude: 87.58, latitude: 43.75, event: '再度出塞，任北庭封常清判官' },
      { year: 757, yearLabel: '757年', placeName: '凤翔', longitude: 107.4, latitude: 34.52, event: '东归赴行在，杜甫举荐为右补阙' },
      { year: 766, yearLabel: '766年', placeName: '嘉州', longitude: 103.76, latitude: 29.55, event: '出为嘉州刺史，世称岑嘉州' },
      { year: 770, yearLabel: '770年', placeName: '成都', longitude: 104.07, latitude: 30.67, event: '罢官后客死成都' },
    ],
    companions: ['杜甫', '高适'],
  },
  {
    id: 'wei-yingwu',
    name: '韦应物',
    dynasty: 'tang',
    epithet: '韦苏州',
    birthYear: 737,
    deathYear: 792,
    lifeLabel: '约737—约792',
    hometown: { placeName: '京兆杜陵', longitude: 108.98, latitude: 34.18 },
    summary: '少为玄宗侍卫，安史乱后折节读书，历任滁、江、苏三州刺史，诗风闲淡。',
    stations: [
      { year: 751, yearLabel: '约751年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '以门荫为三卫郎' },
      { year: 783, yearLabel: '783年', placeName: '滁州', longitude: 118.3, latitude: 32.3, event: '出为滁州刺史' },
      { year: 785, yearLabel: '785年', placeName: '江州', longitude: 115.99, latitude: 29.71, event: '改江州刺史' },
      { year: 788, yearLabel: '788年', placeName: '苏州', longitude: 120.62, latitude: 31.3, event: '任苏州刺史' },
    ],
  },
  {
    id: 'meng-jiao',
    name: '孟郊',
    dynasty: 'tang',
    styleName: '字东野',
    birthYear: 751,
    deathYear: 814,
    lifeLabel: '751—814',
    hometown: { placeName: '湖州武康', longitude: 119.96, latitude: 30.54 },
    summary: '屡试不第，四十六岁方登进士，五十岁任溧阳尉。与韩愈并称韩孟，诗以苦吟见长。',
    stations: [
      { year: 751, yearLabel: '751年', placeName: '武康', longitude: 119.96, latitude: 30.54, event: '生于湖州武康' },
      { year: 796, yearLabel: '796年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '第三次应试，进士及第' },
      { year: 801, yearLabel: '801年', placeName: '溧阳', longitude: 119.48, latitude: 31.42, event: '任溧阳尉，迎母奉养' },
      { year: 814, yearLabel: '814年', placeName: '阌乡', longitude: 110.87, latitude: 34.53, event: '赴兴元途中暴疾卒' },
    ],
    companions: ['韩愈'],
  },
  {
    id: 'han-yu',
    name: '韩愈',
    dynasty: 'tang',
    styleName: '字退之',
    epithet: '韩昌黎',
    birthYear: 768,
    deathYear: 824,
    lifeLabel: '768—824',
    hometown: { placeName: '河阳', longitude: 112.79, latitude: 34.9 },
    summary: '唐宋八大家之首，倡古文运动。两度南贬，谏迎佛骨后远谪潮州，晚年官至吏部侍郎。',
    stations: [
      { year: 768, yearLabel: '768年', placeName: '河阳', longitude: 112.79, latitude: 34.9, event: '生于河阳' },
      { year: 792, yearLabel: '792年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '第四次应试，进士及第' },
      { year: 803, yearLabel: '803年', placeName: '阳山', longitude: 112.64, latitude: 24.47, event: '上疏论天旱人饥，贬阳山令' },
      { year: 819, yearLabel: '819年', placeName: '蓝关', longitude: 109.3, latitude: 34.0, event: '谏迎佛骨，南贬途经蓝关' },
      { year: 819, yearLabel: '819年', placeName: '潮州', longitude: 116.62, latitude: 23.66, event: '任潮州刺史，驱鳄兴学' },
      { year: 824, yearLabel: '824年', placeName: '长安', longitude: 108.9, latitude: 34.05, event: '吏部侍郎任上病逝' },
    ],
    companions: ['孟郊', '柳宗元', '刘禹锡'],
  },
  {
    id: 'liu-yuxi',
    name: '刘禹锡',
    dynasty: 'tang',
    styleName: '字梦得',
    epithet: '诗豪',
    birthYear: 772,
    deathYear: 842,
    lifeLabel: '772—842',
    hometown: { placeName: '洛阳', longitude: 112.45, latitude: 34.62 },
    summary: '永贞革新失败后贬朗州，此后辗转连、夔、和州二十三年。晚年与白居易唱和洛阳。',
    stations: [
      { year: 805, yearLabel: '805年', placeName: '朗州', longitude: 111.7, latitude: 29.03, event: '永贞革新失败，贬朗州司马' },
      { year: 815, yearLabel: '815年', placeName: '连州', longitude: 112.38, latitude: 24.78, event: '玄都观诗得罪，出为连州刺史' },
      { year: 822, yearLabel: '822年', placeName: '夔州', longitude: 109.57, latitude: 31.05, event: '任夔州刺史，作竹枝词' },
      { year: 824, yearLabel: '824年', placeName: '和州', longitude: 118.35, latitude: 31.72, event: '转和州刺史' },
      { year: 826, yearLabel: '826年', placeName: '扬州', longitude: 119.42, latitude: 32.39, event: '罢和州北归，与白居易初逢' },
      { year: 842, yearLabel: '842年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '以太子宾客分司东都，卒' },
    ],
    companions: ['白居易', '柳宗元', '韩愈'],
  },
  {
    id: 'bai-juyi',
    name: '白居易',
    dynasty: 'tang',
    styleName: '字乐天，号香山居士',
    epithet: '诗魔',
    birthYear: 772,
    deathYear: 846,
    lifeLabel: '772—846',
    hometown: { placeName: '郑州新郑', longitude: 113.74, latitude: 34.4 },
    summary: '新乐府运动倡导者。贬江州后由兼济转向独善，历任杭、苏刺史，晚年居洛阳香山。',
    stations: [
      { year: 772, yearLabel: '772年', placeName: '新郑', longitude: 113.74, latitude: 34.4, event: '生于新郑东郭宅' },
      { year: 788, yearLabel: '788年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '少年入京，以诗谒顾况' },
      { year: 806, yearLabel: '806年', placeName: '盩厔', longitude: 108.22, latitude: 34.16, event: '任盩厔尉，作长恨歌' },
      { year: 815, yearLabel: '815年', placeName: '江州', longitude: 115.99, latitude: 29.71, event: '越职言事，贬江州司马' },
      { year: 822, yearLabel: '822年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '任杭州刺史，修筑湖堤' },
      { year: 825, yearLabel: '825年', placeName: '苏州', longitude: 120.62, latitude: 31.3, event: '任苏州刺史' },
      { year: 829, yearLabel: '829年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '分司东都，居履道里至终老' },
    ],
    companions: ['元稹', '刘禹锡'],
  },
  {
    id: 'liu-zongyuan',
    name: '柳宗元',
    dynasty: 'tang',
    styleName: '字子厚',
    epithet: '柳柳州',
    birthYear: 773,
    deathYear: 819,
    lifeLabel: '773—819',
    hometown: { placeName: '河东解县', longitude: 110.83, latitude: 34.92 },
    summary: '永贞革新骨干，贬永州十年，写下永州八记；再贬柳州，死于任所，年仅四十七。',
    stations: [
      { year: 773, yearLabel: '773年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '生于长安' },
      { year: 805, yearLabel: '805年', placeName: '永州', longitude: 111.61, latitude: 26.42, event: '永贞革新失败，贬永州司马' },
      { year: 815, yearLabel: '815年', placeName: '柳州', longitude: 109.41, latitude: 24.33, event: '出为柳州刺史' },
      { year: 819, yearLabel: '819年', placeName: '柳州', longitude: 109.41, latitude: 24.32, event: '卒于柳州任所' },
    ],
    companions: ['刘禹锡', '韩愈'],
  },
  {
    id: 'yuan-zhen',
    name: '元稹',
    dynasty: 'tang',
    styleName: '字微之',
    birthYear: 779,
    deathYear: 831,
    lifeLabel: '779—831',
    hometown: { placeName: '河南洛阳', longitude: 112.45, latitude: 34.62 },
    summary: '与白居易同倡新乐府，并称元白。两人贬所相隔千里，三十年间诗筒往还不绝。',
    stations: [
      { year: 809, yearLabel: '809年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '任监察御史，妻韦丛卒' },
      { year: 815, yearLabel: '815年', placeName: '通州', longitude: 107.5, latitude: 31.21, event: '出为通州司马，闻乐天贬江州' },
      { year: 823, yearLabel: '823年', placeName: '越州', longitude: 120.58, latitude: 30.0, event: '任浙东观察使，与杭州白居易隔江唱和' },
      { year: 831, yearLabel: '831年', placeName: '武昌', longitude: 114.3, latitude: 30.55, event: '武昌军节度使任上暴卒' },
    ],
    companions: ['白居易'],
  },
  {
    id: 'li-he',
    name: '李贺',
    dynasty: 'tang',
    styleName: '字长吉',
    epithet: '诗鬼',
    birthYear: 790,
    deathYear: 816,
    lifeLabel: '790—816',
    hometown: { placeName: '福昌昌谷', longitude: 111.87, latitude: 34.51 },
    summary: '因父名晋肃避讳不得应进士，仅任奉礼郎。诗思瑰诡，二十七岁卒于昌谷。',
    stations: [
      { year: 790, yearLabel: '790年', placeName: '昌谷', longitude: 111.87, latitude: 34.51, event: '生于福昌昌谷' },
      { year: 811, yearLabel: '811年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '任太常寺奉礼郎' },
      { year: 814, yearLabel: '814年', placeName: '潞州', longitude: 113.1, latitude: 36.2, event: '辞官北游，依张彻于潞州' },
      { year: 816, yearLabel: '816年', placeName: '昌谷', longitude: 111.87, latitude: 34.51, event: '病卒故里' },
    ],
    companions: ['韩愈'],
  },
  {
    id: 'du-mu',
    name: '杜牧',
    dynasty: 'tang',
    styleName: '字牧之，号樊川居士',
    epithet: '小杜',
    birthYear: 803,
    deathYear: 852,
    lifeLabel: '803—852',
    hometown: { placeName: '京兆万年 · 樊川', longitude: 108.9, latitude: 34.05 },
    summary: '宰相杜佑之孙。在江南幕府十年，出守黄、池、睦三州，晚年归樊川别墅。',
    stations: [
      { year: 803, yearLabel: '803年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '生于京兆杜氏' },
      { year: 828, yearLabel: '828年', placeName: '洪州', longitude: 115.89, latitude: 28.68, event: '进士及第，入江西沈传师幕' },
      { year: 833, yearLabel: '833年', placeName: '扬州', longitude: 119.43, latitude: 32.39, event: '入淮南牛僧孺幕，掌书记' },
      { year: 842, yearLabel: '842年', placeName: '黄州', longitude: 114.87, latitude: 30.44, event: '出为黄州刺史' },
      { year: 844, yearLabel: '844年', placeName: '池州', longitude: 117.48, latitude: 30.66, event: '转池州刺史' },
      { year: 846, yearLabel: '846年', placeName: '睦州', longitude: 119.28, latitude: 29.6, event: '转睦州刺史' },
      { year: 852, yearLabel: '852年', placeName: '樊川', longitude: 108.9, latitude: 34.05, event: '官中书舍人，卒于樊川别墅' },
    ],
    companions: ['李商隐'],
  },
  {
    id: 'li-shangyin',
    name: '李商隐',
    dynasty: 'tang',
    styleName: '字义山，号玉谿生',
    epithet: '小李',
    birthYear: 813,
    deathYear: 858,
    lifeLabel: '约813—约858',
    hometown: { placeName: '怀州河内', longitude: 112.93, latitude: 35.09 },
    summary: '受知于令狐楚，又娶李党王茂元之女，一生困于牛李党争，辗转诸侯幕府。',
    stations: [
      { year: 829, yearLabel: '829年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '以文谒令狐楚，入其幕府' },
      { year: 837, yearLabel: '837年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '进士及第' },
      { year: 838, yearLabel: '838年', placeName: '泾州', longitude: 107.36, latitude: 35.33, event: '入王茂元幕，娶其女' },
      { year: 847, yearLabel: '847年', placeName: '桂林', longitude: 110.29, latitude: 25.27, event: '随郑亚赴桂管幕府' },
      { year: 851, yearLabel: '851年', placeName: '梓州', longitude: 105.09, latitude: 31.1, event: '入柳仲郢东川幕，前后五年' },
      { year: 858, yearLabel: '约858年', placeName: '郑州', longitude: 113.65, latitude: 34.75, event: '罢职闲居，病卒郑州' },
    ],
    companions: ['杜牧'],
  },
  {
    id: 'wei-zhuang',
    name: '韦庄',
    dynasty: 'five-dynasties',
    styleName: '字端己',
    epithet: '花间词人',
    birthYear: 836,
    deathYear: 910,
    lifeLabel: '约836—910',
    hometown: { placeName: '京兆杜陵', longitude: 108.98, latitude: 34.18 },
    summary: '亲历黄巢入长安，作秦妇吟；避乱江南十年，晚年入蜀，为前蜀开国宰相。',
    stations: [
      { year: 880, yearLabel: '880年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '应举长安，逢黄巢破城' },
      { year: 887, yearLabel: '约887年', placeName: '金陵', longitude: 118.8, latitude: 32.07, event: '避乱江南，往来润州金陵' },
      { year: 894, yearLabel: '894年', placeName: '长安', longitude: 108.95, latitude: 34.26, event: '年近六十进士及第' },
      { year: 901, yearLabel: '901年', placeName: '成都', longitude: 104.07, latitude: 30.67, event: '入蜀为王建掌书记' },
      { year: 910, yearLabel: '910年', placeName: '成都', longitude: 104.03, latitude: 30.66, event: '前蜀宰相任上卒于花林坊' },
    ],
  },
  {
    id: 'li-yu',
    name: '李煜',
    dynasty: 'five-dynasties',
    styleName: '字重光，号钟隐',
    epithet: '词中之帝',
    birthYear: 937,
    deathYear: 978,
    lifeLabel: '937—978',
    hometown: { placeName: '金陵', longitude: 118.78, latitude: 32.06 },
    summary: '南唐末代国主。国破北迁汴京后，词由宫廷绮艳变为亡国之痛，四十二岁被赐死。',
    stations: [
      { year: 937, yearLabel: '937年', placeName: '金陵', longitude: 118.78, latitude: 32.06, event: '生于金陵' },
      { year: 961, yearLabel: '961年', placeName: '金陵', longitude: 118.79, latitude: 32.04, event: '嗣位为南唐国主' },
      { year: 975, yearLabel: '975年', placeName: '金陵', longitude: 118.78, latitude: 32.05, event: '宋军破城，肉袒出降' },
      { year: 976, yearLabel: '976年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '押赴汴京，封违命侯' },
      { year: 978, yearLabel: '978年', placeName: '汴京', longitude: 114.31, latitude: 34.81, event: '七夕被赐牵机药死' },
    ],
    companions: ['李璟'],
  },
  {
    id: 'li-jing',
    name: '李璟',
    dynasty: 'five-dynasties',
    epithet: '南唐中主',
    birthYear: 916,
    deathYear: 961,
    lifeLabel: '916—961',
    hometown: { placeName: '金陵', longitude: 118.78, latitude: 32.06 },
    summary: '南唐第二代国主，好文辞，与冯延巳等君臣唱和。败于后周后割江北，迁都洪州而卒。',
    stations: [
      { year: 943, yearLabel: '943年', placeName: '金陵', longitude: 118.78, latitude: 32.06, event: '即位为南唐国主' },
      { year: 958, yearLabel: '958年', placeName: '金陵', longitude: 118.79, latitude: 32.05, event: '淮南之战败，割江北十四州' },
      { year: 961, yearLabel: '961年', placeName: '洪州', longitude: 115.89, latitude: 28.68, event: '迁都南昌，同年卒' },
    ],
    companions: ['李煜'],
  },
  {
    id: 'liu-yong',
    name: '柳永',
    dynasty: 'song',
    styleName: '原名三变，字耆卿',
    epithet: '白衣卿相',
    birthYear: 984,
    deathYear: 1053,
    lifeLabel: '约984—约1053',
    hometown: { placeName: '崇安五夫里', longitude: 118.03, latitude: 27.76 },
    summary: '大量创制慢词，流连汴京坊曲，屡试不第，年近五十方登第，官至屯田员外郎。',
    stations: [
      { year: 1003, yearLabel: '约1003年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '赴京途中滞留杭州，作望海潮' },
      { year: 1009, yearLabel: '约1009年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '入汴应试，屡试不第' },
      { year: 1034, yearLabel: '1034年', placeName: '睦州', longitude: 119.28, latitude: 29.6, event: '进士及第，任睦州团练推官' },
      { year: 1053, yearLabel: '约1053年', placeName: '润州', longitude: 119.45, latitude: 32.2, event: '卒于润州' },
    ],
  },
  {
    id: 'fan-zhongyan',
    name: '范仲淹',
    dynasty: 'song',
    styleName: '字希文',
    epithet: '范文正公',
    birthYear: 989,
    deathYear: 1052,
    lifeLabel: '989—1052',
    hometown: { placeName: '苏州吴县', longitude: 120.62, latitude: 31.3 },
    summary: '二岁而孤，苦读应天书院。守西北边四年，主持庆历新政，先天下之忧而忧。',
    stations: [
      { year: 1011, yearLabel: '1011年', placeName: '应天府', longitude: 115.65, latitude: 34.45, event: '苦读应天书院' },
      { year: 1015, yearLabel: '1015年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '进士及第' },
      { year: 1040, yearLabel: '1040年', placeName: '延州', longitude: 109.49, latitude: 36.6, event: '知延州，御西夏' },
      { year: 1043, yearLabel: '1043年', placeName: '汴京', longitude: 114.32, latitude: 34.8, event: '参知政事，推行庆历新政' },
      { year: 1046, yearLabel: '1046年', placeName: '邓州', longitude: 112.09, latitude: 32.69, event: '知邓州，作岳阳楼记' },
      { year: 1052, yearLabel: '1052年', placeName: '徐州', longitude: 117.18, latitude: 34.26, event: '赴颍州途中卒于徐州' },
    ],
    companions: ['欧阳修', '晏殊'],
  },
  {
    id: 'yan-shu',
    name: '晏殊',
    dynasty: 'song',
    styleName: '字同叔',
    birthYear: 991,
    deathYear: 1055,
    lifeLabel: '991—1055',
    hometown: { placeName: '抚州临川', longitude: 116.36, latitude: 27.95 },
    summary: '十四岁以神童入试，仕途平顺，官至宰相，奖掖范仲淹、欧阳修等后进。',
    stations: [
      { year: 1005, yearLabel: '1005年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '以神童召试，赐同进士出身' },
      { year: 1027, yearLabel: '1027年', placeName: '应天府', longitude: 115.65, latitude: 34.45, event: '知应天府，延范仲淹掌书院' },
      { year: 1042, yearLabel: '1042年', placeName: '汴京', longitude: 114.32, latitude: 34.8, event: '拜相' },
      { year: 1055, yearLabel: '1055年', placeName: '汴京', longitude: 114.31, latitude: 34.81, event: '病卒京师' },
    ],
    companions: ['范仲淹', '欧阳修'],
  },
  {
    id: 'ouyang-xiu',
    name: '欧阳修',
    dynasty: 'song',
    styleName: '字永叔，号醉翁、六一居士',
    birthYear: 1007,
    deathYear: 1072,
    lifeLabel: '1007—1072',
    hometown: { placeName: '吉州庐陵', longitude: 115.44, latitude: 27.32 },
    summary: '北宋文坛领袖，主持嘉祐贡举拔擢苏轼兄弟、曾巩。两贬夷陵、滁州，晚年退居颍州。',
    stations: [
      { year: 1007, yearLabel: '1007年', placeName: '绵州', longitude: 104.73, latitude: 31.47, event: '生于绵州推官任所' },
      { year: 1030, yearLabel: '1030年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '进士及第' },
      { year: 1036, yearLabel: '1036年', placeName: '夷陵', longitude: 111.29, latitude: 30.7, event: '为范仲淹辩护，贬夷陵令' },
      { year: 1045, yearLabel: '1045年', placeName: '滁州', longitude: 118.3, latitude: 32.3, event: '庆历新政失败，出知滁州' },
      { year: 1057, yearLabel: '1057年', placeName: '汴京', longitude: 114.32, latitude: 34.8, event: '知贡举，取苏轼、苏辙、曾巩' },
      { year: 1072, yearLabel: '1072年', placeName: '颍州', longitude: 115.81, latitude: 32.9, event: '致仕退居颍州西湖，卒' },
    ],
    companions: ['苏轼', '范仲淹', '晏殊', '王安石'],
  },
  {
    id: 'wang-anshi',
    name: '王安石',
    dynasty: 'song',
    styleName: '字介甫，号半山',
    epithet: '王荆公',
    birthYear: 1021,
    deathYear: 1086,
    lifeLabel: '1021—1086',
    hometown: { placeName: '抚州临川', longitude: 116.36, latitude: 27.95 },
    summary: '熙宁变法主持者，两度拜相两度罢相，晚年退居江宁钟山，诗风转为精工闲淡。',
    stations: [
      { year: 1042, yearLabel: '1042年', placeName: '扬州', longitude: 119.43, latitude: 32.39, event: '进士及第，签书淮南判官' },
      { year: 1047, yearLabel: '1047年', placeName: '鄞县', longitude: 121.55, latitude: 29.87, event: '知鄞县，兴水利' },
      { year: 1050, yearLabel: '1050年', placeName: '越州', longitude: 120.58, latitude: 30.01, event: '鄞县任满途经越州，登飞来峰' },
      { year: 1069, yearLabel: '1069年', placeName: '汴京', longitude: 114.32, latitude: 34.8, event: '参知政事，推行新法' },
      { year: 1075, yearLabel: '1075年', placeName: '瓜洲', longitude: 119.0, latitude: 32.2, event: '复相赴京，舟泊瓜洲' },
      { year: 1076, yearLabel: '1076年', placeName: '江宁', longitude: 118.84, latitude: 32.06, event: '再度罢相，退居钟山半山园至终' },
    ],
    companions: ['苏轼', '欧阳修'],
  },
  {
    id: 'su-shi',
    name: '苏轼',
    dynasty: 'song',
    styleName: '字子瞻，号东坡居士',
    epithet: '坡仙',
    birthYear: 1037,
    deathYear: 1101,
    lifeLabel: '1037—1101',
    hometown: { placeName: '眉州眉山', longitude: 103.83, latitude: 30.05 },
    summary: '一生宦游与贬谪交替，自谓平生功业在黄州、惠州、儋州。所到之处留下诗词、堤坝与传说。',
    stations: [
      { year: 1037, yearLabel: '1037年', placeName: '眉山', longitude: 103.83, latitude: 30.05, event: '生于眉州眉山纱縠行' },
      { year: 1057, yearLabel: '1057年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '与弟苏辙同榜进士' },
      { year: 1071, yearLabel: '1071年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '反对新法，通判杭州' },
      { year: 1074, yearLabel: '1074年', placeName: '密州', longitude: 119.41, latitude: 35.99, event: '知密州' },
      { year: 1077, yearLabel: '1077年', placeName: '徐州', longitude: 117.18, latitude: 34.26, event: '知徐州，抗洪筑堤' },
      { year: 1080, yearLabel: '1080年', placeName: '黄州', longitude: 114.87, latitude: 30.44, event: '乌台诗案后贬黄州团练副使' },
      { year: 1089, yearLabel: '1089年', placeName: '杭州', longitude: 120.15, latitude: 30.24, event: '再守杭州，疏浚西湖筑苏堤' },
      { year: 1094, yearLabel: '1094年', placeName: '惠州', longitude: 114.41, latitude: 23.11, event: '新党复起，远贬惠州' },
      { year: 1097, yearLabel: '1097年', placeName: '儋州', longitude: 109.58, latitude: 19.52, event: '再贬儋州，渡海南' },
      { year: 1101, yearLabel: '1101年', placeName: '常州', longitude: 119.97, latitude: 31.81, event: '遇赦北还，卒于常州' },
    ],
    companions: ['欧阳修', '王安石', '黄庭坚', '秦观'],
  },
  {
    id: 'huang-tingjian',
    name: '黄庭坚',
    dynasty: 'song',
    styleName: '字鲁直，号山谷道人',
    epithet: '江西诗派之宗',
    birthYear: 1045,
    deathYear: 1105,
    lifeLabel: '1045—1105',
    hometown: { placeName: '洪州分宁', longitude: 114.55, latitude: 29.03 },
    summary: '苏门四学士之一，与苏轼并称苏黄。晚年因党争两度远贬黔、宜，卒于宜州羁所。',
    stations: [
      { year: 1045, yearLabel: '1045年', placeName: '分宁', longitude: 114.55, latitude: 29.03, event: '生于洪州分宁' },
      { year: 1080, yearLabel: '1080年', placeName: '太和', longitude: 114.9, latitude: 26.79, event: '知吉州太和县，登快阁' },
      { year: 1085, yearLabel: '1085年', placeName: '德平镇', longitude: 116.9, latitude: 37.47, event: '监德州德平镇' },
      { year: 1086, yearLabel: '1086年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '入秘书省，与苏轼唱和' },
      { year: 1095, yearLabel: '1095年', placeName: '黔州', longitude: 108.77, latitude: 29.53, event: '修实录得罪，贬黔州' },
      { year: 1105, yearLabel: '1105年', placeName: '宜州', longitude: 108.65, latitude: 24.49, event: '除名羁管宜州，卒' },
    ],
    companions: ['苏轼', '秦观'],
  },
  {
    id: 'qin-guan',
    name: '秦观',
    dynasty: 'song',
    styleName: '字少游',
    epithet: '淮海居士',
    birthYear: 1049,
    deathYear: 1100,
    lifeLabel: '1049—1100',
    hometown: { placeName: '扬州高邮', longitude: 119.46, latitude: 32.78 },
    summary: '苏门四学士之一，婉约词宗。新党执政后一路南贬郴州、横州、雷州，北归途中卒于藤州。',
    stations: [
      { year: 1049, yearLabel: '1049年', placeName: '高邮', longitude: 119.46, latitude: 32.78, event: '生于高邮' },
      { year: 1085, yearLabel: '1085年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '进士及第' },
      { year: 1096, yearLabel: '1096年', placeName: '郴州', longitude: 113.02, latitude: 25.77, event: '削秩编管郴州' },
      { year: 1098, yearLabel: '1098年', placeName: '雷州', longitude: 110.08, latitude: 20.91, event: '再徙横州、雷州' },
      { year: 1100, yearLabel: '1100年', placeName: '藤州', longitude: 110.92, latitude: 23.37, event: '赦归途中卒于藤州' },
    ],
    companions: ['苏轼', '黄庭坚'],
  },
  {
    id: 'zhou-bangyan',
    name: '周邦彦',
    dynasty: 'song',
    styleName: '字美成，号清真居士',
    birthYear: 1056,
    deathYear: 1121,
    lifeLabel: '1056—1121',
    hometown: { placeName: '钱塘', longitude: 120.17, latitude: 30.26 },
    summary: '以太学生献汴都赋得官，精通音律，提举大晟府，格律派词人之宗。',
    stations: [
      { year: 1078, yearLabel: '1078年', placeName: '汴京', longitude: 114.34, latitude: 34.8, event: '入汴京为太学生' },
      { year: 1087, yearLabel: '1087年', placeName: '庐州', longitude: 117.27, latitude: 31.86, event: '出为庐州教授' },
      { year: 1116, yearLabel: '1116年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '提举大晟府' },
      { year: 1121, yearLabel: '1121年', placeName: '南京应天府', longitude: 115.65, latitude: 34.45, event: '卒于应天府' },
    ],
  },
  {
    id: 'li-qingzhao',
    name: '李清照',
    dynasty: 'song',
    epithet: '易安居士',
    birthYear: 1084,
    deathYear: 1155,
    lifeLabel: '1084—约1155',
    hometown: { placeName: '齐州章丘', longitude: 117.53, latitude: 36.72 },
    summary: '前半生与赵明诚在汴京、青州收藏金石；靖康之变后南渡，夫死书散，流寓江浙。',
    stations: [
      { year: 1084, yearLabel: '1084年', placeName: '章丘', longitude: 117.53, latitude: 36.72, event: '生于齐州章丘明水' },
      { year: 1101, yearLabel: '1101年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '嫁太学生赵明诚' },
      { year: 1107, yearLabel: '1107年', placeName: '青州', longitude: 118.48, latitude: 36.69, event: '屏居青州归来堂十年' },
      { year: 1128, yearLabel: '1128年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '南渡建康，明年明诚病卒' },
      { year: 1134, yearLabel: '1134年', placeName: '金华', longitude: 119.65, latitude: 29.08, event: '避兵金华，泛舟双溪之念' },
      { year: 1155, yearLabel: '约1155年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '晚年寓居临安' },
    ],
  },
  {
    id: 'yue-fei',
    name: '岳飞',
    dynasty: 'song',
    styleName: '字鹏举',
    birthYear: 1103,
    deathYear: 1142,
    lifeLabel: '1103—1142',
    hometown: { placeName: '相州汤阴', longitude: 114.36, latitude: 35.92 },
    summary: '南宋抗金名将，收复襄阳六郡，郾城大捷后被十二道金牌召回，以莫须有罪名遇害。',
    stations: [
      { year: 1103, yearLabel: '1103年', placeName: '汤阴', longitude: 114.36, latitude: 35.92, event: '生于相州汤阴' },
      { year: 1134, yearLabel: '1134年', placeName: '襄阳', longitude: 112.14, latitude: 32.02, event: '收复襄阳六郡' },
      { year: 1135, yearLabel: '1135年', placeName: '鄂州', longitude: 114.31, latitude: 30.54, event: '移屯鄂州，岳家军大本营' },
      { year: 1140, yearLabel: '1140年', placeName: '郾城', longitude: 114.0, latitude: 33.59, event: '郾城大捷，旋被召回' },
      { year: 1142, yearLabel: '1142年', placeName: '临安', longitude: 120.16, latitude: 30.26, event: '遇害于大理寺狱' },
    ],
  },
  {
    id: 'lu-you',
    name: '陆游',
    dynasty: 'song',
    styleName: '字务观，号放翁',
    birthYear: 1125,
    deathYear: 1210,
    lifeLabel: '1125—1210',
    hometown: { placeName: '越州山阴', longitude: 120.58, latitude: 30.0 },
    summary: '存诗九千余首。四十八岁到南郑前线是他一生最高光的八个月，此后半生在山阴回望铁马秋风。',
    stations: [
      { year: 1125, yearLabel: '1125年', placeName: '淮河舟中', longitude: 118.5, latitude: 33.2, event: '生于父亲赴任的淮河舟中' },
      { year: 1154, yearLabel: '1154年', placeName: '临安', longitude: 120.17, latitude: 30.26, event: '锁厅试第一，因秦桧而被黜' },
      { year: 1170, yearLabel: '1170年', placeName: '夔州', longitude: 109.57, latitude: 31.05, event: '溯江入蜀，任夔州通判' },
      { year: 1172, yearLabel: '1172年', placeName: '南郑', longitude: 107.02, latitude: 33.07, event: '入王炎幕，亲临川陕前线' },
      { year: 1173, yearLabel: '1173年', placeName: '成都', longitude: 104.07, latitude: 30.67, event: '入范成大幕，自号放翁' },
      { year: 1186, yearLabel: '1186年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '起知严州，入都陛辞' },
      { year: 1190, yearLabel: '1190年', placeName: '山阴', longitude: 120.58, latitude: 30.0, event: '罢归三山，闲居二十年至终' },
    ],
    companions: ['范成大', '杨万里', '辛弃疾'],
  },
  {
    id: 'fan-chengda',
    name: '范成大',
    dynasty: 'song',
    styleName: '字致能，号石湖居士',
    birthYear: 1126,
    deathYear: 1193,
    lifeLabel: '1126—1193',
    hometown: { placeName: '平江吴县', longitude: 120.62, latitude: 31.3 },
    summary: '出使金国不辱使命，历知静江、成都、明州，晚年退居石湖写田园杂兴。',
    stations: [
      { year: 1154, yearLabel: '1154年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '进士及第' },
      { year: 1170, yearLabel: '1170年', placeName: '燕京', longitude: 116.4, latitude: 39.9, event: '出使金国，途经汴京州桥' },
      { year: 1173, yearLabel: '1173年', placeName: '桂林', longitude: 110.29, latitude: 25.27, event: '知静江府' },
      { year: 1175, yearLabel: '1175年', placeName: '成都', longitude: 104.07, latitude: 30.67, event: '任四川制置使，辟陆游为参议' },
      { year: 1186, yearLabel: '1186年', placeName: '石湖', longitude: 120.57, latitude: 31.22, event: '退居石湖，作四时田园杂兴' },
    ],
    companions: ['陆游', '杨万里'],
  },
  {
    id: 'yang-wanli',
    name: '杨万里',
    dynasty: 'song',
    styleName: '字廷秀，号诚斋',
    birthYear: 1127,
    deathYear: 1206,
    lifeLabel: '1127—1206',
    hometown: { placeName: '吉州吉水', longitude: 115.14, latitude: 27.21 },
    summary: '中兴四大诗人之一，创诚斋体，以口语写小景，童趣与生机扑面。',
    stations: [
      { year: 1154, yearLabel: '1154年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '进士及第' },
      { year: 1178, yearLabel: '1178年', placeName: '常州', longitude: 119.97, latitude: 31.81, event: '知常州，诗风一变' },
      { year: 1187, yearLabel: '1187年', placeName: '临安', longitude: 120.15, latitude: 30.23, event: '任秘书少监，晓出净慈寺' },
      { year: 1192, yearLabel: '1192年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '任江东转运副使' },
      { year: 1206, yearLabel: '1206年', placeName: '吉水', longitude: 115.14, latitude: 27.21, event: '闲居南溪十五年，忧愤而卒' },
    ],
    companions: ['陆游', '范成大'],
  },
  {
    id: 'zhu-xi',
    name: '朱熹',
    dynasty: 'song',
    styleName: '字元晦，号晦庵',
    epithet: '朱子',
    birthYear: 1130,
    deathYear: 1200,
    lifeLabel: '1130—1200',
    hometown: { placeName: '徽州婺源', longitude: 117.86, latitude: 29.25 },
    summary: '理学集大成者，生于尤溪，长于五夫里，复兴白鹿洞书院，晚年讲学于建阳考亭。',
    stations: [
      { year: 1130, yearLabel: '1130年', placeName: '尤溪', longitude: 118.19, latitude: 26.17, event: '生于南剑州尤溪' },
      { year: 1143, yearLabel: '1143年', placeName: '五夫里', longitude: 118.03, latitude: 27.76, event: '父卒，依刘子羽居崇安五夫里' },
      { year: 1179, yearLabel: '1179年', placeName: '南康军', longitude: 115.99, latitude: 29.44, event: '知南康军，复兴白鹿洞书院' },
      { year: 1183, yearLabel: '1183年', placeName: '武夷山', longitude: 118.0, latitude: 27.65, event: '筑武夷精舍讲学' },
      { year: 1200, yearLabel: '1200年', placeName: '建阳考亭', longitude: 118.12, latitude: 27.33, event: '庆元党禁中卒于考亭' },
    ],
    companions: ['辛弃疾'],
  },
  {
    id: 'xin-qiji',
    name: '辛弃疾',
    dynasty: 'song',
    styleName: '字幼安，号稼轩',
    epithet: '词中之龙',
    birthYear: 1140,
    deathYear: 1207,
    lifeLabel: '1140—1207',
    hometown: { placeName: '济南历城', longitude: 117.0, latitude: 36.67 },
    summary: '生于金占区，二十二岁起兵抗金、南归宋廷。一生力主北伐却多被闲置，在上饶、铅山隐居近二十年。',
    stations: [
      { year: 1140, yearLabel: '1140年', placeName: '历城', longitude: 117.0, latitude: 36.67, event: '生于金人治下的济南' },
      { year: 1162, yearLabel: '1162年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '率五十骑擒叛将张安国，南归' },
      { year: 1176, yearLabel: '1176年', placeName: '赣州', longitude: 114.93, latitude: 25.83, event: '任江西提点刑狱，过造口' },
      { year: 1182, yearLabel: '1182年', placeName: '带湖', longitude: 117.93, latitude: 28.45, event: '落职，退居上饶带湖' },
      { year: 1194, yearLabel: '1194年', placeName: '瓢泉', longitude: 117.7, latitude: 28.3, event: '移居铅山瓢泉' },
      { year: 1204, yearLabel: '1204年', placeName: '京口', longitude: 119.43, latitude: 32.21, event: '知镇江府，登北固亭' },
      { year: 1207, yearLabel: '1207年', placeName: '铅山', longitude: 117.7, latitude: 28.31, event: '病卒铅山，临终大呼杀贼' },
    ],
    companions: ['陆游', '朱熹'],
  },
  {
    id: 'jiang-kui',
    name: '姜夔',
    dynasty: 'song',
    styleName: '字尧章',
    epithet: '白石道人',
    birthYear: 1155,
    deathYear: 1221,
    lifeLabel: '约1155—约1221',
    hometown: { placeName: '饶州鄱阳', longitude: 116.68, latitude: 29.0 },
    summary: '布衣终身，精通音律，自度曲并留有旁谱。二十出头过兵燹后的扬州，写下扬州慢。',
    stations: [
      { year: 1176, yearLabel: '1176年', placeName: '扬州', longitude: 119.42, latitude: 32.4, event: '冬至过维扬，作扬州慢' },
      { year: 1187, yearLabel: '1187年', placeName: '湖州', longitude: 120.09, latitude: 30.89, event: '随萧德藻寓居湖州' },
      { year: 1196, yearLabel: '1196年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '移家杭州，依张鉴' },
    ],
    companions: ['杨万里', '范成大'],
  },
  {
    id: 'wen-tianxiang',
    name: '文天祥',
    dynasty: 'song',
    styleName: '字宋瑞，号文山',
    birthYear: 1236,
    deathYear: 1283,
    lifeLabel: '1236—1283',
    hometown: { placeName: '吉州庐陵', longitude: 114.99, latitude: 27.11 },
    summary: '状元宰相，元兵南下时起兵勤王，兵败被俘，拒降四年后在大都柴市就义。',
    stations: [
      { year: 1256, yearLabel: '1256年', placeName: '临安', longitude: 120.17, latitude: 30.27, event: '殿试第一' },
      { year: 1275, yearLabel: '1275年', placeName: '赣州', longitude: 114.93, latitude: 25.83, event: '毁家纾难，起兵勤王' },
      { year: 1276, yearLabel: '1276年', placeName: '京口', longitude: 119.43, latitude: 32.21, event: '使元被扣，自京口脱险' },
      { year: 1278, yearLabel: '1278年', placeName: '海丰', longitude: 115.32, latitude: 22.97, event: '五坡岭兵败被俘' },
      { year: 1279, yearLabel: '1279年', placeName: '伶仃洋', longitude: 113.74, latitude: 22.17, event: '被押过零丁洋，目睹崖山' },
      { year: 1279, yearLabel: '1279年', placeName: '大庾岭', longitude: 114.36, latitude: 25.4, event: '北押途经南安军' },
      { year: 1283, yearLabel: '1283年', placeName: '大都', longitude: 116.4, latitude: 39.92, event: '柴市就义' },
    ],
  },
  {
    id: 'zhang-yanghao',
    name: '张养浩',
    dynasty: 'yuan',
    styleName: '字希孟，号云庄',
    birthYear: 1270,
    deathYear: 1329,
    lifeLabel: '1270—1329',
    hometown: { placeName: '济南', longitude: 117.0, latitude: 36.67 },
    summary: '元代名臣，辞官归济南隐居八年。关中大旱时受命赈灾，沿途写下怀古散曲，到任四月卒。',
    stations: [
      { year: 1270, yearLabel: '1270年', placeName: '济南', longitude: 117.0, latitude: 36.67, event: '生于济南' },
      { year: 1311, yearLabel: '1311年', placeName: '大都', longitude: 116.4, latitude: 39.9, event: '拜监察御史，上万言书' },
      { year: 1321, yearLabel: '1321年', placeName: '济南', longitude: 117.01, latitude: 36.68, event: '辞官归隐云庄' },
      { year: 1329, yearLabel: '1329年', placeName: '潼关', longitude: 110.25, latitude: 34.54, event: '赴陕西赈灾，途经潼关、骊山' },
      { year: 1329, yearLabel: '1329年', placeName: '奉元', longitude: 108.95, latitude: 34.27, event: '赈灾积劳，卒于任上' },
    ],
  },
  {
    id: 'yu-qian',
    name: '于谦',
    dynasty: 'ming',
    styleName: '字廷益',
    birthYear: 1398,
    deathYear: 1457,
    lifeLabel: '1398—1457',
    hometown: { placeName: '杭州钱塘', longitude: 120.16, latitude: 30.25 },
    summary: '巡抚河南、山西十八年。土木之变后力主守城，指挥北京保卫战；英宗复辟后被冤杀。',
    stations: [
      { year: 1398, yearLabel: '1398年', placeName: '钱塘', longitude: 120.16, latitude: 30.25, event: '生于杭州' },
      { year: 1421, yearLabel: '1421年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '进士及第' },
      { year: 1430, yearLabel: '1430年', placeName: '太原', longitude: 112.55, latitude: 37.87, event: '巡抚河南、山西' },
      { year: 1449, yearLabel: '1449年', placeName: '北京', longitude: 116.41, latitude: 39.91, event: '土木之变后指挥京师保卫战' },
      { year: 1457, yearLabel: '1457年', placeName: '北京', longitude: 116.39, latitude: 39.9, event: '夺门之变后被害' },
    ],
  },
  {
    id: 'tang-yin',
    name: '唐寅',
    dynasty: 'ming',
    styleName: '字伯虎，号六如居士',
    birthYear: 1470,
    deathYear: 1524,
    lifeLabel: '1470—1524',
    hometown: { placeName: '苏州吴县', longitude: 120.62, latitude: 31.3 },
    summary: '应天乡试解元，次年因科场案绝意仕进，筑桃花庵，以卖画为生。',
    stations: [
      { year: 1498, yearLabel: '1498年', placeName: '南京', longitude: 118.78, latitude: 32.06, event: '应天乡试第一' },
      { year: 1499, yearLabel: '1499年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '会试卷入科场舞弊案' },
      { year: 1505, yearLabel: '1505年', placeName: '桃花坞', longitude: 120.62, latitude: 31.3, event: '筑桃花庵，卖画为生' },
    ],
  },
  {
    id: 'yang-shen',
    name: '杨慎',
    dynasty: 'ming',
    styleName: '字用修，号升庵',
    birthYear: 1488,
    deathYear: 1559,
    lifeLabel: '1488—1559',
    hometown: { placeName: '成都新都', longitude: 104.16, latitude: 30.82 },
    summary: '正德状元，大礼议中两遭廷杖，谪戍云南永昌卫三十余年，著述百余种，老死戍所。',
    stations: [
      { year: 1511, yearLabel: '1511年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '殿试第一' },
      { year: 1524, yearLabel: '1524年', placeName: '北京', longitude: 116.39, latitude: 39.91, event: '大礼议中哭谏，受廷杖' },
      { year: 1525, yearLabel: '1525年', placeName: '永昌卫', longitude: 99.16, latitude: 25.12, event: '谪戍永昌卫' },
      { year: 1559, yearLabel: '1559年', placeName: '永昌卫', longitude: 99.17, latitude: 25.11, event: '卒于戍所' },
    ],
  },
  {
    id: 'qi-jiguang',
    name: '戚继光',
    dynasty: 'ming',
    styleName: '字元敬',
    birthYear: 1528,
    deathYear: 1588,
    lifeLabel: '1528—1588',
    hometown: { placeName: '登州', longitude: 120.75, latitude: 37.8 },
    summary: '在浙闽练戚家军平倭，后镇守蓟州十六年，修筑长城敌台。',
    stations: [
      { year: 1528, yearLabel: '1528年', placeName: '登州', longitude: 120.75, latitude: 37.8, event: '袭登州卫指挥佥事' },
      { year: 1561, yearLabel: '1561年', placeName: '台州', longitude: 121.43, latitude: 28.66, event: '台州大捷，九战皆胜' },
      { year: 1568, yearLabel: '1568年', placeName: '蓟州', longitude: 117.41, latitude: 40.05, event: '总理蓟州练兵，修敌台' },
      { year: 1588, yearLabel: '1588年', placeName: '登州', longitude: 120.75, latitude: 37.81, event: '罢归故里，病卒' },
    ],
  },
  {
    id: 'xia-wanchun',
    name: '夏完淳',
    dynasty: 'ming',
    styleName: '字存古',
    birthYear: 1631,
    deathYear: 1647,
    lifeLabel: '1631—1647',
    hometown: { placeName: '松江华亭', longitude: 121.23, latitude: 31.03 },
    summary: '十四岁随父起兵抗清，父死后继续抗争，十七岁在南京就义。',
    stations: [
      { year: 1645, yearLabel: '1645年', placeName: '松江', longitude: 121.23, latitude: 31.03, event: '随父夏允彝起兵抗清' },
      { year: 1647, yearLabel: '1647年', placeName: '南京', longitude: 118.78, latitude: 32.06, event: '被捕押解南京，就义' },
    ],
  },
  {
    id: 'gu-yanwu',
    name: '顾炎武',
    dynasty: 'qing',
    styleName: '字宁人，号亭林',
    birthYear: 1613,
    deathYear: 1682,
    lifeLabel: '1613—1682',
    hometown: { placeName: '苏州昆山', longitude: 120.98, latitude: 31.38 },
    summary: '明亡后参与抗清，此后二十余年北游考察山川关塞，著日知录、天下郡国利病书。',
    stations: [
      { year: 1645, yearLabel: '1645年', placeName: '昆山', longitude: 120.98, latitude: 31.38, event: '参加昆山抗清' },
      { year: 1657, yearLabel: '1657年', placeName: '山东', longitude: 117.0, latitude: 36.67, event: '北游，往来鲁冀晋陕' },
      { year: 1677, yearLabel: '1677年', placeName: '华阴', longitude: 110.09, latitude: 34.57, event: '定居华阴' },
      { year: 1682, yearLabel: '1682年', placeName: '曲沃', longitude: 111.47, latitude: 35.64, event: '卒于曲沃' },
    ],
  },
  {
    id: 'nalan-xingde',
    name: '纳兰性德',
    dynasty: 'qing',
    styleName: '字容若，号楞伽山人',
    birthYear: 1655,
    deathYear: 1685,
    lifeLabel: '1655—1685',
    hometown: { placeName: '京师', longitude: 116.38, latitude: 39.94 },
    summary: '权臣明珠长子，康熙一等侍卫，扈从出塞，词以真情哀感见称，三十一岁病逝。',
    stations: [
      { year: 1676, yearLabel: '1676年', placeName: '京师', longitude: 116.38, latitude: 39.94, event: '殿试二甲进士，授三等侍卫' },
      { year: 1682, yearLabel: '1682年', placeName: '山海关', longitude: 119.75, latitude: 40.0, event: '扈从康熙东巡出关' },
      { year: 1683, yearLabel: '1683年', placeName: '古北口', longitude: 117.0, latitude: 40.69, event: '扈驾塞外' },
      { year: 1685, yearLabel: '1685年', placeName: '京师', longitude: 116.38, latitude: 39.95, event: '寒疾病卒' },
    ],
  },
  {
    id: 'zheng-xie',
    name: '郑燮',
    dynasty: 'qing',
    styleName: '字克柔，号板桥',
    birthYear: 1693,
    deathYear: 1766,
    lifeLabel: '1693—1766',
    hometown: { placeName: '扬州兴化', longitude: 119.85, latitude: 32.93 },
    summary: '扬州八怪之一，诗书画三绝。任范县、潍县知县十二年，因为民请赈得罪上司而去官。',
    stations: [
      { year: 1693, yearLabel: '1693年', placeName: '兴化', longitude: 119.85, latitude: 32.93, event: '生于兴化' },
      { year: 1736, yearLabel: '1736年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '进士及第' },
      { year: 1746, yearLabel: '1746年', placeName: '潍县', longitude: 119.1, latitude: 36.7, event: '调知潍县，逢大饥开仓' },
      { year: 1753, yearLabel: '1753年', placeName: '扬州', longitude: 119.43, latitude: 32.39, event: '罢官，回扬州卖画' },
    ],
  },
  {
    id: 'yuan-mei',
    name: '袁枚',
    dynasty: 'qing',
    styleName: '字子才，号随园老人',
    birthYear: 1716,
    deathYear: 1798,
    lifeLabel: '1716—1798',
    hometown: { placeName: '杭州钱塘', longitude: 120.16, latitude: 30.25 },
    summary: '倡性灵说。三十三岁辞官，在江宁小仓山筑随园，优游其中近五十年。',
    stations: [
      { year: 1739, yearLabel: '1739年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '进士及第' },
      { year: 1745, yearLabel: '1745年', placeName: '江宁', longitude: 118.78, latitude: 32.06, event: '任江宁知县' },
      { year: 1749, yearLabel: '1749年', placeName: '随园', longitude: 118.74, latitude: 32.06, event: '辞官，隐居随园至终' },
    ],
  },
  {
    id: 'gong-zizhen',
    name: '龚自珍',
    dynasty: 'qing',
    styleName: '字璱人，号定庵',
    birthYear: 1792,
    deathYear: 1841,
    lifeLabel: '1792—1841',
    hometown: { placeName: '杭州仁和', longitude: 120.17, latitude: 30.28 },
    summary: '近代思想先驱。1839年辞官南归，一路写成三百一十五首己亥杂诗，两年后暴卒丹阳。',
    stations: [
      { year: 1829, yearLabel: '1829年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '第六次会试方中进士' },
      { year: 1839, yearLabel: '1839年', placeName: '北京', longitude: 116.45, latitude: 39.89, event: '辞官出京，始作己亥杂诗' },
      { year: 1839, yearLabel: '1839年', placeName: '镇江', longitude: 119.42, latitude: 32.21, event: '南归途经镇江' },
      { year: 1841, yearLabel: '1841年', placeName: '丹阳', longitude: 119.6, latitude: 32.0, event: '主讲云阳书院，暴卒' },
    ],
  },
  {
    id: 'lin-zexu',
    name: '林则徐',
    dynasty: 'qing',
    styleName: '字元抚，号少穆',
    birthYear: 1785,
    deathYear: 1850,
    lifeLabel: '1785—1850',
    hometown: { placeName: '福州侯官', longitude: 119.3, latitude: 26.08 },
    summary: '以钦差大臣赴广州禁烟，虎门销烟。鸦片战争后被革职遣戍伊犁，兴修水利。',
    stations: [
      { year: 1785, yearLabel: '1785年', placeName: '福州', longitude: 119.3, latitude: 26.08, event: '生于侯官' },
      { year: 1839, yearLabel: '1839年', placeName: '虎门', longitude: 113.67, latitude: 22.8, event: '虎门销烟' },
      { year: 1842, yearLabel: '1842年', placeName: '西安', longitude: 108.95, latitude: 34.26, event: '遣戍伊犁，西安登程' },
      { year: 1843, yearLabel: '1842—1845年', placeName: '伊犁', longitude: 81.3, latitude: 43.9, event: '戍伊犁，勘垦南疆' },
      { year: 1850, yearLabel: '1850年', placeName: '普宁', longitude: 116.17, latitude: 23.3, event: '赴广西途中卒于潮州普宁' },
    ],
  },
  {
    id: 'tan-sitong',
    name: '谭嗣同',
    dynasty: 'qing',
    styleName: '字复生',
    birthYear: 1865,
    deathYear: 1898,
    lifeLabel: '1865—1898',
    hometown: { placeName: '湖南浏阳', longitude: 113.63, latitude: 28.14 },
    summary: '戊戌六君子之一。少年随父宦游甘肃，过潼关有诗；变法失败后拒绝出走，就义菜市口。',
    stations: [
      { year: 1865, yearLabel: '1865年', placeName: '北京', longitude: 116.4, latitude: 39.9, event: '生于北京宣武城南' },
      { year: 1878, yearLabel: '1878年', placeName: '潼关', longitude: 110.25, latitude: 34.54, event: '随父赴甘肃，过潼关' },
      { year: 1897, yearLabel: '1897年', placeName: '长沙', longitude: 112.94, latitude: 28.23, event: '办时务学堂、南学会' },
      { year: 1898, yearLabel: '1898年', placeName: '北京', longitude: 116.39, latitude: 39.89, event: '戊戌政变，就义菜市口' },
    ],
  },
  {
    id: 'qiu-jin',
    name: '秋瑾',
    dynasty: 'qing',
    styleName: '字璿卿，号鉴湖女侠',
    birthYear: 1875,
    deathYear: 1907,
    lifeLabel: '1875—1907',
    hometown: { placeName: '绍兴山阴', longitude: 120.58, latitude: 30.0 },
    summary: '冲破家庭东渡日本留学，加入同盟会，回国主持大通学堂，起义事泄就义于绍兴轩亭口。',
    stations: [
      { year: 1875, yearLabel: '1875年', placeName: '厦门', longitude: 118.08, latitude: 24.48, event: '生于福建厦门' },
      { year: 1903, yearLabel: '1903年', placeName: '北京', longitude: 116.39, latitude: 39.9, event: '随夫居京师，中秋作满江红' },
      { year: 1906, yearLabel: '1906年', placeName: '上海', longitude: 121.47, latitude: 31.23, event: '自日本归国，办中国女报' },
      { year: 1907, yearLabel: '1907年', placeName: '绍兴', longitude: 120.58, latitude: 30.0, event: '主持大通学堂，事泄就义' },
    ],
  },
  {
    id: 'xiang-yu',
    name: '项羽',
    dynasty: 'han',
    styleName: '名籍，字羽',
    epithet: '西楚霸王',
    birthYear: -232,
    deathYear: -202,
    lifeLabel: '前232—前202',
    hometown: { placeName: '下相', longitude: 118.28, latitude: 33.96 },
    summary: '楚国名将项燕之孙，随叔父项梁起兵吴中，巨鹿破秦，自立西楚霸王，楚汉相争败于垓下。',
    stations: [
      { year: -232, yearLabel: '前232年', placeName: '下相', longitude: 118.28, latitude: 33.96, event: '生于下相，楚将之后' },
      { year: -209, yearLabel: '前209年', placeName: '吴中', longitude: 120.62, latitude: 31.3, event: '随项梁起兵会稽' },
      { year: -207, yearLabel: '前207年', placeName: '巨鹿', longitude: 115.03, latitude: 37.22, event: '破釜沉舟，大破秦军' },
      { year: -206, yearLabel: '前206年', placeName: '彭城', longitude: 117.18, latitude: 34.26, event: '自立西楚霸王，都彭城' },
      { year: -202, yearLabel: '前202年', placeName: '垓下', longitude: 117.63, latitude: 33.35, event: '四面楚歌，作垓下歌' },
      { year: -202, yearLabel: '前202年', placeName: '乌江', longitude: 118.04, latitude: 31.73, event: '不肯过江东，自刎乌江' },
    ],
  },
  {
    id: 'liu-che',
    name: '刘彻',
    dynasty: 'han',
    epithet: '汉武帝',
    birthYear: -156,
    deathYear: -87,
    lifeLabel: '前156—前87',
    hometown: { placeName: '汉长安城', longitude: 108.87, latitude: 34.3 },
    summary: '西汉第七位皇帝，北击匈奴、通西域，立乐府采诗，《秋风辞》是其传世辞作。',
    stations: [
      { year: -156, yearLabel: '前156年', placeName: '长安', longitude: 108.87, latitude: 34.3, event: '生于长安' },
      { year: -141, yearLabel: '前141年', placeName: '长安', longitude: 108.87, latitude: 34.3, event: '即位，后立乐府采诗' },
      { year: -113, yearLabel: '前113年', placeName: '汾阴', longitude: 110.55, latitude: 35.43, event: '祠后土，泛舟汾河作秋风辞' },
      { year: -110, yearLabel: '前110年', placeName: '泰山', longitude: 117.1, latitude: 36.25, event: '东巡封禅泰山' },
      { year: -87, yearLabel: '前87年', placeName: '五柞宫', longitude: 108.3, latitude: 34.1, event: '崩于盩厔五柞宫' },
    ],
  },
  {
    id: 'ruan-ji',
    name: '阮籍',
    dynasty: 'wei-jin',
    styleName: '字嗣宗',
    epithet: '竹林七贤',
    birthYear: 210,
    deathYear: 263,
    lifeLabel: '210—263',
    hometown: { placeName: '陈留尉氏', longitude: 114.19, latitude: 34.41 },
    summary: '建安七子阮瑀之子，身处魏晋易代，以醉酒佯狂避祸，《咏怀》八十二首隐晦深远。',
    stations: [
      { year: 210, yearLabel: '210年', placeName: '尉氏', longitude: 114.19, latitude: 34.41, event: '生于陈留尉氏' },
      { year: 249, yearLabel: '249年', placeName: '洛阳', longitude: 112.62, latitude: 34.72, event: '高平陵之变后为司马懿从事中郎' },
      { year: 255, yearLabel: '约255年', placeName: '东平', longitude: 116.3, latitude: 35.9, event: '自求为东平相，旬日而还' },
      { year: 263, yearLabel: '263年', placeName: '洛阳', longitude: 112.62, latitude: 34.72, event: '任步兵校尉后卒' },
    ],
  },
  {
    id: 'zuo-si',
    name: '左思',
    dynasty: 'wei-jin',
    styleName: '字太冲',
    birthYear: 250,
    deathYear: 305,
    lifeLabel: '约250—约305',
    hometown: { placeName: '齐国临淄', longitude: 118.3, latitude: 36.8 },
    summary: '出身寒门，貌寝口讷，十年构思《三都赋》致洛阳纸贵；《咏史》八首直指门阀制度。',
    stations: [
      { year: 250, yearLabel: '约250年', placeName: '临淄', longitude: 118.3, latitude: 36.8, event: '生于齐国临淄' },
      { year: 272, yearLabel: '约272年', placeName: '洛阳', longitude: 112.62, latitude: 34.72, event: '妹左芬入宫，移家京师' },
      { year: 285, yearLabel: '约285年', placeName: '洛阳', longitude: 112.62, latitude: 34.72, event: '三都赋成，洛阳纸贵' },
      { year: 303, yearLabel: '303年', placeName: '冀州', longitude: 115.57, latitude: 37.55, event: '张方纵暴洛阳，举家适冀州' },
    ],
  },
  {
    id: 'yu-xin',
    name: '庾信',
    dynasty: 'southern-northern',
    styleName: '字子山',
    epithet: '庾开府',
    birthYear: 513,
    deathYear: 581,
    lifeLabel: '513—581',
    hometown: { placeName: '南阳新野', longitude: 112.36, latitude: 32.52 },
    summary: '前半生为梁朝宫体诗人，出使西魏被留北方，终身未能南归，晚年诗赋苍凉，杜甫称其“暮年诗赋动江关”。',
    stations: [
      { year: 531, yearLabel: '约531年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '任东宫抄撰学士，与徐陵并称' },
      { year: 548, yearLabel: '548年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '侯景之乱，率军守朱雀航而溃' },
      { year: 552, yearLabel: '552年', placeName: '江陵', longitude: 112.24, latitude: 30.33, event: '投奔梁元帝于江陵' },
      { year: 554, yearLabel: '554年', placeName: '长安', longitude: 108.9, latitude: 34.3, event: '出使西魏，江陵陷落被留' },
      { year: 581, yearLabel: '581年', placeName: '长安', longitude: 108.9, latitude: 34.3, event: '卒于北方，未得南归' },
    ],
  },
  {
    id: 'yang-guang',
    name: '杨广',
    dynasty: 'sui',
    epithet: '隋炀帝',
    birthYear: 569,
    deathYear: 618,
    lifeLabel: '569—618',
    hometown: { placeName: '长安', longitude: 108.93, latitude: 34.27 },
    summary: '隋朝第二位皇帝，开凿大运河、营建东都，三下江都，终在江都兵变中被杀。',
    stations: [
      { year: 569, yearLabel: '569年', placeName: '长安', longitude: 108.93, latitude: 34.27, event: '生于长安' },
      { year: 589, yearLabel: '589年', placeName: '建康', longitude: 118.78, latitude: 32.06, event: '统军平陈，入建康' },
      { year: 590, yearLabel: '590年', placeName: '江都', longitude: 119.42, latitude: 32.39, event: '任扬州总管，镇江都十年' },
      { year: 605, yearLabel: '605年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '即位后营建东都，开通济渠' },
      { year: 609, yearLabel: '609年', placeName: '张掖', longitude: 100.45, latitude: 38.93, event: '西巡河西，会见西域诸国' },
      { year: 618, yearLabel: '618年', placeName: '江都', longitude: 119.42, latitude: 32.39, event: '江都兵变被弑' },
    ],
  },
  {
    id: 'luo-binwang',
    name: '骆宾王',
    dynasty: 'tang',
    styleName: '字观光',
    epithet: '初唐四杰',
    birthYear: 626,
    deathYear: 684,
    lifeLabel: '约626—684后',
    hometown: { placeName: '婺州义乌', longitude: 120.07, latitude: 29.31 },
    summary: '七岁咏鹅，初唐四杰之一。曾从军西域，任侍御史时下狱，后随徐敬业起兵讨武，檄文传诵一时，兵败后下落不明。',
    stations: [
      { year: 626, yearLabel: '约626年', placeName: '义乌', longitude: 120.07, latitude: 29.31, event: '生于婺州义乌' },
      { year: 670, yearLabel: '670年', placeName: '西域', longitude: 89.19, latitude: 42.95, event: '从军西域，出塞诗多作于此时' },
      { year: 678, yearLabel: '678年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '任侍御史，下狱作在狱咏蝉' },
      { year: 684, yearLabel: '684年', placeName: '扬州', longitude: 119.42, latitude: 32.39, event: '随徐敬业起兵，作讨武曌檄' },
    ],
    companions: ['王勃'],
  },
  {
    id: 'wang-zhihuan',
    name: '王之涣',
    dynasty: 'tang',
    styleName: '字季凌',
    birthYear: 688,
    deathYear: 742,
    lifeLabel: '688—742',
    hometown: { placeName: '绛州', longitude: 111.22, latitude: 35.62 },
    summary: '以边塞诗著称，传世仅六首。曾任冀州衡水主簿，因被诬辞官漫游十五年，晚年任文安县尉。',
    stations: [
      { year: 688, yearLabel: '688年', placeName: '绛州', longitude: 111.22, latitude: 35.62, event: '生于绛州，祖籍晋阳' },
      { year: 723, yearLabel: '约723年', placeName: '衡水', longitude: 115.7, latitude: 37.73, event: '任冀州衡水主簿' },
      { year: 727, yearLabel: '约727年', placeName: '蒲州', longitude: 110.33, latitude: 34.83, event: '辞官漫游黄河南北' },
      { year: 741, yearLabel: '741年', placeName: '文安', longitude: 116.46, latitude: 38.87, event: '补文安县尉，次年卒于官' },
    ],
    companions: ['王昌龄', '高适'],
  },
  {
    id: 'cui-hao',
    name: '崔颢',
    dynasty: 'tang',
    birthYear: 704,
    deathYear: 754,
    lifeLabel: '约704—754',
    hometown: { placeName: '汴州', longitude: 114.31, latitude: 34.8 },
    summary: '开元进士，早年诗多浮艳，后游历江南、从军河东，诗风转为雄浑；《黄鹤楼》传为李白所叹服。',
    stations: [
      { year: 723, yearLabel: '723年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '登进士第' },
      { year: 733, yearLabel: '约733年', placeName: '武昌', longitude: 114.3, latitude: 30.55, event: '游江南，登黄鹤楼' },
      { year: 740, yearLabel: '约740年', placeName: '河东', longitude: 112.55, latitude: 37.87, event: '入河东军幕，写边塞诗' },
      { year: 754, yearLabel: '754年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '官司勋员外郎，卒于任' },
    ],
    companions: ['李白'],
  },
  {
    id: 'zhang-ji-tang',
    name: '张继',
    dynasty: 'tang',
    styleName: '字懿孙',
    birthYear: 715,
    deathYear: 779,
    lifeLabel: '约715—约779',
    hometown: { placeName: '襄州', longitude: 112.14, latitude: 32.04 },
    summary: '天宝进士，安史之乱时避居江南，一首《枫桥夜泊》使姑苏城外寒山寺名传后世。',
    stations: [
      { year: 753, yearLabel: '753年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '登进士第' },
      { year: 756, yearLabel: '约756年', placeName: '苏州', longitude: 120.57, latitude: 31.32, event: '避乱江南，夜泊枫桥' },
      { year: 770, yearLabel: '约770年', placeName: '洪州', longitude: 115.88, latitude: 28.68, event: '任盐铁判官，分掌财赋' },
      { year: 779, yearLabel: '约779年', placeName: '洪州', longitude: 115.88, latitude: 28.68, event: '与妻同卒于洪州' },
    ],
  },
  {
    id: 'jia-dao',
    name: '贾岛',
    dynasty: 'tang',
    styleName: '字阆仙',
    epithet: '诗奴',
    birthYear: 779,
    deathYear: 843,
    lifeLabel: '779—843',
    hometown: { placeName: '范阳', longitude: 115.97, latitude: 39.49 },
    summary: '早年为僧，法号无本，以推敲苦吟闻名；受韩愈赏识还俗应举，屡试不第，终于小吏。与孟郊并称“郊寒岛瘦”。',
    stations: [
      { year: 779, yearLabel: '779年', placeName: '范阳', longitude: 115.97, latitude: 39.49, event: '生于范阳，早年出家' },
      { year: 810, yearLabel: '约810年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '以诗谒韩愈，结识孟郊' },
      { year: 811, yearLabel: '约811年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '还俗入京应举，屡试不第' },
      { year: 837, yearLabel: '837年', placeName: '长江县', longitude: 105.72, latitude: 30.57, event: '贬长江县主簿' },
      { year: 843, yearLabel: '843年', placeName: '普州', longitude: 105.34, latitude: 30.1, event: '迁普州司仓参军，卒于任' },
    ],
    companions: ['韩愈', '孟郊'],
  },
  {
    id: 'li-shen',
    name: '李绅',
    dynasty: 'tang',
    styleName: '字公垂',
    birthYear: 772,
    deathYear: 846,
    lifeLabel: '772—846',
    hometown: { placeName: '润州无锡', longitude: 120.3, latitude: 31.57 },
    summary: '新乐府运动的倡导者之一，与元稹、白居易交游；早年《悯农》流传极广，后官至宰相。',
    stations: [
      { year: 772, yearLabel: '772年', placeName: '无锡', longitude: 120.3, latitude: 31.57, event: '长于润州无锡' },
      { year: 806, yearLabel: '806年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '登进士第' },
      { year: 834, yearLabel: '834年', placeName: '越州', longitude: 120.58, latitude: 30.0, event: '任浙东观察使' },
      { year: 842, yearLabel: '842年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '拜相' },
      { year: 846, yearLabel: '846年', placeName: '扬州', longitude: 119.42, latitude: 32.39, event: '卒于淮南节度使任' },
    ],
    companions: ['元稹', '白居易'],
  },
  {
    id: 'wen-tingyun',
    name: '温庭筠',
    dynasty: 'tang',
    styleName: '字飞卿',
    epithet: '花间鼻祖',
    birthYear: 812,
    deathYear: 866,
    lifeLabel: '约812—约866',
    hometown: { placeName: '太原祁县', longitude: 112.33, latitude: 37.36 },
    summary: '才思敏捷，八叉手而成八韵，却因恃才傲物屡试不第。词风秾艳，被奉为花间派之祖，与李商隐并称“温李”。',
    stations: [
      { year: 840, yearLabel: '约840年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '入京应举，屡试不第' },
      { year: 858, yearLabel: '约858年', placeName: '随县', longitude: 113.37, latitude: 31.72, event: '贬随县尉' },
      { year: 859, yearLabel: '约859年', placeName: '商山', longitude: 109.94, latitude: 33.87, event: '离京途经商山，作商山早行' },
      { year: 866, yearLabel: '866年', placeName: '长安', longitude: 108.95, latitude: 34.27, event: '任国子助教，不久卒' },
    ],
    companions: ['李商隐'],
  },
  {
    id: 'chen-yuyi',
    name: '陈与义',
    dynasty: 'song',
    styleName: '字去非，号简斋',
    birthYear: 1090,
    deathYear: 1138,
    lifeLabel: '1090—1138',
    hometown: { placeName: '洛阳', longitude: 112.45, latitude: 34.62 },
    summary: '北宋末以诗得徽宗赏识，靖康之变后南奔五年，历经湖湘岭南到达临安，诗风由清新转为沉郁。',
    stations: [
      { year: 1090, yearLabel: '1090年', placeName: '洛阳', longitude: 112.45, latitude: 34.62, event: '生于洛阳' },
      { year: 1113, yearLabel: '1113年', placeName: '汴京', longitude: 114.31, latitude: 34.8, event: '上舍甲科及第' },
      { year: 1116, yearLabel: '约1116年', placeName: '襄邑', longitude: 115.07, latitude: 34.45, event: '往来汴京东南，舟行襄邑道中' },
      { year: 1128, yearLabel: '1128年', placeName: '岳州', longitude: 113.13, latitude: 29.37, event: '靖康乱后南奔，寓居岳阳' },
      { year: 1131, yearLabel: '1131年', placeName: '会稽', longitude: 120.58, latitude: 30.0, event: '抵行在，除兵部员外郎' },
      { year: 1138, yearLabel: '1138年', placeName: '湖州', longitude: 120.49, latitude: 30.73, event: '退居湖州青墩，卒' },
    ],
  },
  {
    id: 'jiang-jie',
    name: '蒋捷',
    dynasty: 'song',
    styleName: '字胜欲，号竹山',
    epithet: '樱桃进士',
    birthYear: 1245,
    deathYear: 1305,
    lifeLabel: '约1245—约1305',
    hometown: { placeName: '阳羡', longitude: 119.82, latitude: 31.36 },
    summary: '宋末进士，宋亡后深怀故国之痛，隐居不仕，漂泊太湖一带，以“红了樱桃，绿了芭蕉”得名。',
    stations: [
      { year: 1274, yearLabel: '1274年', placeName: '临安', longitude: 120.17, latitude: 30.25, event: '登进士第，不久宋亡' },
      { year: 1276, yearLabel: '约1276年', placeName: '吴江', longitude: 120.64, latitude: 31.16, event: '元兵南下，舟行流离太湖' },
      { year: 1290, yearLabel: '约1290年', placeName: '竹山', longitude: 119.82, latitude: 31.36, event: '隐居阳羡竹山，拒不仕元' },
    ],
  },
  {
    id: 'guan-hanqing',
    name: '关汉卿',
    dynasty: 'yuan',
    styleName: '号已斋叟',
    epithet: '杂剧班头',
    birthYear: 1220,
    deathYear: 1300,
    lifeLabel: '约1220—约1300',
    hometown: { placeName: '大都', longitude: 116.38, latitude: 39.93 },
    summary: '元杂剧奠基人，玉京书会才人，编写《窦娥冤》等六十余种杂剧，自称“普天下郎君领袖，盖世界浪子班头”。',
    stations: [
      { year: 1260, yearLabel: '约1260年', placeName: '大都', longitude: 116.38, latitude: 39.93, event: '活跃于玉京书会' },
      { year: 1279, yearLabel: '约1279年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '南宋亡后南游杭州' },
      { year: 1300, yearLabel: '约1300年', placeName: '大都', longitude: 116.38, latitude: 39.93, event: '晚年仍在大都' },
    ],
  },
  {
    id: 'ma-zhiyuan',
    name: '马致远',
    dynasty: 'yuan',
    styleName: '号东篱',
    epithet: '秋思之祖',
    birthYear: 1250,
    deathYear: 1321,
    lifeLabel: '约1250—约1321',
    hometown: { placeName: '大都', longitude: 116.38, latitude: 39.93 },
    summary: '元曲四大家之一，早年求仕，任江浙行省务官，中年后看破功名、归隐田园，以《天净沙·秋思》著称。',
    stations: [
      { year: 1270, yearLabel: '约1270年', placeName: '大都', longitude: 116.38, latitude: 39.93, event: '早年在大都求取功名' },
      { year: 1285, yearLabel: '约1285年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '任江浙行省务官' },
      { year: 1300, yearLabel: '约1300年', placeName: '杭州', longitude: 120.15, latitude: 30.25, event: '退隐田园，与元贞书会唱和' },
    ],
    companions: ['关汉卿'],
  },
  {
    id: 'wang-mian',
    name: '王冕',
    dynasty: 'yuan',
    styleName: '字元章，号煮石山农',
    birthYear: 1287,
    deathYear: 1359,
    lifeLabel: '1287—1359',
    hometown: { placeName: '诸暨', longitude: 120.23, latitude: 29.71 },
    summary: '农家出身，放牛时窃听学舍读书，屡试不第后以画梅卖画为生，北游大都后预见天下将乱，归隐九里山。',
    stations: [
      { year: 1287, yearLabel: '1287年', placeName: '诸暨', longitude: 120.23, latitude: 29.71, event: '生于诸暨农家' },
      { year: 1348, yearLabel: '约1348年', placeName: '大都', longitude: 116.38, latitude: 39.93, event: '北游大都，拒荐馆职' },
      { year: 1350, yearLabel: '约1350年', placeName: '九里山', longitude: 120.23, latitude: 29.71, event: '归隐九里山，种梅千株' },
      { year: 1359, yearLabel: '1359年', placeName: '会稽', longitude: 120.58, latitude: 30.0, event: '朱元璋军入越后病卒' },
    ],
  },
  {
    id: 'gao-qi',
    name: '高启',
    dynasty: 'ming',
    styleName: '字季迪，号青丘子',
    birthYear: 1336,
    deathYear: 1374,
    lifeLabel: '1336—1374',
    hometown: { placeName: '长洲', longitude: 120.62, latitude: 31.3 },
    summary: '元末明初吴中四杰之首，隐居青丘，洪武初应召修《元史》，辞官归里后因魏观案被腰斩。',
    stations: [
      { year: 1336, yearLabel: '1336年', placeName: '长洲', longitude: 120.62, latitude: 31.3, event: '生于平江长洲' },
      { year: 1360, yearLabel: '约1360年', placeName: '青丘', longitude: 120.9, latitude: 31.35, event: '隐居吴淞江畔青丘' },
      { year: 1369, yearLabel: '1369年', placeName: '南京', longitude: 118.78, latitude: 32.06, event: '应召修元史' },
      { year: 1370, yearLabel: '1370年', placeName: '青丘', longitude: 120.9, latitude: 31.35, event: '辞户部右侍郎归里' },
      { year: 1374, yearLabel: '1374年', placeName: '南京', longitude: 118.78, latitude: 32.06, event: '坐魏观案被腰斩' },
    ],
  },
  {
    id: 'wang-shizhen',
    name: '王士禛',
    dynasty: 'qing',
    styleName: '字子真，号渔洋山人',
    epithet: '神韵说',
    birthYear: 1634,
    deathYear: 1711,
    lifeLabel: '1634—1711',
    hometown: { placeName: '新城', longitude: 118.1, latitude: 36.96 },
    summary: '清初诗坛领袖，倡神韵说。任扬州推官五年，红桥修禊名动江南，后官至刑部尚书。',
    stations: [
      { year: 1634, yearLabel: '1634年', placeName: '新城', longitude: 118.1, latitude: 36.96, event: '生于山东新城' },
      { year: 1660, yearLabel: '1660年', placeName: '扬州', longitude: 119.42, latitude: 32.39, event: '任扬州推官，往来真州' },
      { year: 1665, yearLabel: '1665年', placeName: '北京', longitude: 116.38, latitude: 39.9, event: '入京为官，主盟诗坛' },
      { year: 1704, yearLabel: '1704年', placeName: '新城', longitude: 118.1, latitude: 36.96, event: '罢官归里' },
    ],
  },
  {
    id: 'zhao-yi',
    name: '赵翼',
    dynasty: 'qing',
    styleName: '字云崧，号瓯北',
    birthYear: 1727,
    deathYear: 1814,
    lifeLabel: '1727—1814',
    hometown: { placeName: '常州阳湖', longitude: 119.95, latitude: 31.8 },
    summary: '乾隆探花，曾任广西镇安知府，辞官后著《廿二史札记》；论诗主创新，与袁枚、蒋士铨并称乾隆三大家。',
    stations: [
      { year: 1727, yearLabel: '1727年', placeName: '阳湖', longitude: 119.95, latitude: 31.8, event: '生于常州阳湖' },
      { year: 1761, yearLabel: '1761年', placeName: '北京', longitude: 116.38, latitude: 39.9, event: '殿试一甲第三名' },
      { year: 1766, yearLabel: '1766年', placeName: '镇安', longitude: 106.62, latitude: 23.33, event: '出任广西镇安知府' },
      { year: 1773, yearLabel: '1773年', placeName: '阳湖', longitude: 119.95, latitude: 31.8, event: '辞官归里著述' },
    ],
    companions: ['袁枚'],
  },
  {
    id: 'huang-jingren',
    name: '黄景仁',
    dynasty: 'qing',
    styleName: '字仲则',
    birthYear: 1749,
    deathYear: 1783,
    lifeLabel: '1749—1783',
    hometown: { placeName: '常州武进', longitude: 119.96, latitude: 31.78 },
    summary: '四岁丧父，家贫多病，一生游幕，诗多悲苦，“十有九人堪白眼，百无一用是书生”广为流传。',
    stations: [
      { year: 1749, yearLabel: '1749年', placeName: '常州', longitude: 119.96, latitude: 31.78, event: '生于常州武进' },
      { year: 1771, yearLabel: '1771年', placeName: '太平府', longitude: 118.5, latitude: 31.56, event: '入安徽学政朱筠幕，采石矶赋诗' },
      { year: 1773, yearLabel: '1773年', placeName: '常州', longitude: 119.96, latitude: 31.78, event: '除夕归家，作癸巳除夕偶成' },
      { year: 1776, yearLabel: '1776年', placeName: '北京', longitude: 116.38, latitude: 39.9, event: '入京谋职，困顿京华' },
      { year: 1783, yearLabel: '1783年', placeName: '解州', longitude: 111.0, latitude: 35.03, event: '贫病交加，卒于赴陕途中' },
    ],
  },
]

export const poetByName = new Map(poets.map((poet) => [poet.name, poet]))
